import { ModelBase, ModelState } from '../../core/model/model-base';
import { Article } from './article';
import { ArticleTimeUnitKind } from './const/enums';
import { getNumberOfDays } from '../../core/helper/date-time';
import { MonthNumber } from '../../core/model/const/enums';
import { LocalDate } from '../../core/model/local-date';
import { Money } from '../../core/model/money';

export enum IndexClause {
  FastighetsAgarna = 0,
  KPI80Plus = 1,
  KPI80PlusQuarter = 3,
  Kommunforbundet = 4,
  KPI80Minimum3Units = 5,
  KPI2020FastighetsAgarna = 10,
  KPI2020Plus = 11,
  KPI2020PlusQuarter = 13,
  KPI2020Custom = 100,
  KPI1980Custom = 101
}

export class LeaseRow extends ModelBase {
  constructor(currencyCode?: string) {
    super();
    this.article = new Article();
    this.quantity = 1;
    this.currencyCode = currencyCode;

    Object.defineProperties(this, {
      _fromDate: { writable: true, enumerable: false },
      _toDate: { writable: true, enumerable: false },
      _currentDuePeriodFrom: { writable: true, enumerable: false },
      _currentDuePeriodTo: { writable: true, enumerable: false },
      _fixedPercentageIncreaseStart: { writable: true, enumerable: false },
      _voided: { writable: true, enumerable: false },
    });
  }

  currencyCode: string;
  private _fromDate: LocalDate;
  private _toDate: LocalDate;
  private _currentDuePeriodFrom: LocalDate;
  private _currentDuePeriodTo: LocalDate;
  private _fixedPercentageIncreaseStart: LocalDate;
  private _voided = false;

  amount: Money;
  baseIndexAmount: Money;
  baseYearRent: Money;
  baseIndexMonth: MonthNumber;
  baseIndexYear: number;
  baseIndexValue: string;
  article: Article;
  indexAmount: Money;
  indexMonth: MonthNumber;
  indexShare: number;
  fixedPercentageIncrease: number;
  indexYear: number;
  indexValue: string;
  indexClause: IndexClause;
  newIndexRentFrom: number;
  indexMinYearlyPercent: number;
  indexMaxYearlyPercent: number;
  rentalObjectId: string;
  invoiceRowGroupId: string;
  invoiceRowGroupCaption: string;
  indexClauseSettingsId: string;

  get indexClauseText(): string {
    switch (this.indexClause) {
      case IndexClause.FastighetsAgarna:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_FAST';
      case IndexClause.KPI80Plus:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS';
      case IndexClause.KPI80PlusQuarter:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_PLUS_QUARTER';
      case IndexClause.Kommunforbundet:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_KOMMUNFORBUNDET';
      case IndexClause.KPI80Minimum3Units:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI80_MINIMUM3UNITS';
      case IndexClause.KPI2020FastighetsAgarna:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_FAST';
      case IndexClause.KPI2020Plus:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS';
      case IndexClause.KPI2020PlusQuarter:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_PLUS_QUARTER';
      case IndexClause.KPI2020Custom:
      case IndexClause.KPI1980Custom:
        return 'LEASE_AGREEMENT_ROW.INDEX_CLAUSE_KPI2020_CUSTOM';
      default:
        return null;
    }
  }

  chargedMonths: MonthNumber[] = [
    MonthNumber.Januari,
    MonthNumber.Februrary,
    MonthNumber.March,
    MonthNumber.April,
    MonthNumber.May,
    MonthNumber.June,
    MonthNumber.Juli,
    MonthNumber.August,
    MonthNumber.September,
    MonthNumber.October,
    MonthNumber.November,
    MonthNumber.December,
  ];

  oneTimeFee = false;
  hasBeenInvoiced = false;

  get fromDate(): LocalDate {
    return this._fromDate;
  }
  set fromDate(value: LocalDate) {
    this._fromDate = LocalDate.fromDate(value);
  }

  get voided(): boolean {
    return this._voided || this._fromDate > this._toDate;
  }

  set voided(value: boolean) {
    this._voided = value;
  }

  get chargedMonthsLength(): number {
    return this.chargedMonths.length;
  }

  get periodAmount(): Money {
    const distributedAmount = new Money(this.yearRent.currencyCode, (this.yearRent.amount * 12) / this.chargedMonths.length);

    if (this.oneTimeFee) return new Money(distributedAmount.currencyCode, distributedAmount.amount / 12).round(2);

    switch (this.article.timeUnit) {
      case ArticleTimeUnitKind.Hour:
        return new Money(distributedAmount.currencyCode, (distributedAmount.amount / getNumberOfDays()) * 24).round(2);
      case ArticleTimeUnitKind.Day:
        return new Money(distributedAmount.currencyCode, distributedAmount.amount / getNumberOfDays()).round(2);
      case ArticleTimeUnitKind.Month:
        return new Money(distributedAmount.currencyCode, distributedAmount.amount / 12).round(2);
      case ArticleTimeUnitKind.Quarter:
        return new Money(distributedAmount.currencyCode, distributedAmount.amount / 4).round(2);
      case ArticleTimeUnitKind.Year:
        return new Money(distributedAmount.currencyCode, distributedAmount.amount).round(2);
    }
  }

  get yearRentPerSquareMeter(): Money {
    if (this.rentalObjectSize > 0) return new Money(this.yearRent.currencyCode, this.yearRent.amount / this.rentalObjectSize).round(2);
  }

  get periodVatAmount(): Money {
    return new Money(this.periodAmount.currencyCode, (this.periodAmount.amount * this.article.vatPercent) / 100).round(2);
  }
  get periodTotalAmount(): Money {
    return new Money(this.periodAmount.currencyCode, this.periodAmount.amount + this.periodVatAmount.amount);
  }
  state: ModelState;
  get toDate(): LocalDate {
    return this._toDate;
  }
  set toDate(value: LocalDate) {
    this._toDate = LocalDate.fromDate(value);
  }
  get fixedPercentageIncreaseStart(): LocalDate {
    return this._fixedPercentageIncreaseStart;
  }
  set fixedPercentageIncreaseStart(value: LocalDate) {
    this._fixedPercentageIncreaseStart = LocalDate.fromDate(value);
  }
  invoiceText: string;
  quantity: number = 0;
  yearRent = new Money(null, null);
  yearRentInclVat = new Money(null, null);
  yearRentInCurrentYear = new Money(null, null);
  periodRent = new Money(null, null);
  periodRentInclVat = new Money(null, null);
  rentalObjectSize: number = 0;
  leaseAgreementInvoicePeriod: number = 0;
  note: string;
  project: string;
  rentThisPeriod: number = 0;
  rentThisPeriodInclVat: number = 0;

  get currentDuePeriodFrom(): LocalDate {
    return this._currentDuePeriodFrom;
  }
  set currentDuePeriodFrom(value: LocalDate) {
    this._currentDuePeriodFrom = LocalDate.fromDate(value);
  }
  get currentDuePeriodTo(): LocalDate {
    return this._currentDuePeriodTo;
  }
  set currentDuePeriodTo(value: LocalDate) {
    this._currentDuePeriodTo = LocalDate.fromDate(value);
  }

  toJSON() {
    return {
      id: this.id,
      amount: this.amount,
      baseIndexAmount: this.baseIndexAmount,
      baseYearRent: this.baseYearRent,
      baseIndexYear: this.baseIndexYear,
      baseIndexMonth: this.baseIndexMonth,
      currentDuePeriodFrom: this.currentDuePeriodFrom,
      currentDuePeriodTo: this.currentDuePeriodTo,
      indexShare: this.indexShare,
      fixedPercentageIncrease: this.fixedPercentageIncrease,
      fixedPercentageIncreaseStart: this.fixedPercentageIncreaseStart,
      article: this.article,
      fromDate: this.fromDate,
      indexYear: this.indexYear,
      indexMonth: this.indexMonth,
      invoiceText: this.invoiceText,
      oneTimeFee: this.oneTimeFee,
      quantity: this.quantity,
      toDate: this.toDate,
      yearRent: this.yearRent,
      chargedMonths: this.chargedMonths,
      indexClause: this.indexClause,
      indexMinYearlyPercent: this.indexMinYearlyPercent,
      indexMaxYearlyPercent: this.indexMaxYearlyPercent,
      rentalObjectId: this.rentalObjectId,
      project: this.project,
      note: this.note,
      invoiceRowGroupId: this.invoiceRowGroupId,
      newIndexRentFrom: this.newIndexRentFrom,
      indexClauseSettingsId: this.indexClauseSettingsId,
    };
  }
}
